// src/client/renderer/vfx/items/lightning-dash.ts
//
// Plan 17, Task 3.2 — Lightning Dash pool.
//
// Audio:
//   The item VFX manager fires the cue only for phase='cast'. Trail and hit
//   emitters are silent so Lightning Dash does not replay the cast sound.
//
// Triggered by THREE events:
//   1. ItemCastEvent (itemId='lightning_dash') → phase='cast':
//        Anticipation flash / electric aura at caster position (200ms).
//        Max-active = 2. One-shot.
//
//   2. ItemDashEvent (itemId='lightning_dash') → phase='active':
//        Path-trail: electric jagged line connecting materialized node positions.
//        SINGLE-INSTANCE per caster — only one active trail per casterId.
//        Re-casting releases prior trail first (F6 release-first via Map<casterId>).
//        Trail lifetime: 900ms, opacity 1→0.
//
//   3. ItemDashHitEvent (itemId='lightning_dash') → phase='impact':
//        Flash sprite at victim's position. 450ms, scale 0→2.6, opacity 1→0.
//        Max-active = 8 (one per simultaneous victim).
//
// Colour: 0xfff099 (electric yellow — same palette as Thunder Fist).
//
// Pool: cast aura (one-shot, max-active = 2).
//       trail (single-instance per casterId, Map<casterId, {emitter, state}>).
//       hit-flash (one-shot, max-active = 8).
//
// GSAP: use tl.call(fn, undefined, absolutePos) — NOT eventCallback('onComplete').
//
// Registry tick contract (HF1):
//   pool.update(_deltaSeconds) IGNORES dt — does NOT call registry.update().
//   The caller (item-vfx-manager at Task 6.1) advances the shared registry once
//   per frame across all pools.

import * as THREE from 'three';
import type { ItemVfxPool, ItemVfxPoolDeps, ItemVfxSpawnArgs } from './item-vfx-types';
import type {
  GameEvent,
  ItemCastEvent,
  ItemDashEvent,
  ItemDashHitEvent,
} from '../../../../shared/game/types';
import { disposeSkillshotGroup } from './skillshot-shared';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/** Max simultaneously active cast-aura emitters. */
const MAX_CAST_ACTIVE = 2;

/** Max simultaneously active hit-flash emitters. */
const MAX_FLASH_ACTIVE = 8;

/** Electric yellow — matches Thunder Fist palette (both electric-themed). */
const ELECTRIC_COLOUR = 0xfff099;

/** Cast aura lifetime. Longer than the old sparkle so the zip reads clearly. */
const CAST_LIFETIME = 0.42;

/** Trail lifetime. Long enough for the deferred dash movement to leave a readable streak. */
const TRAIL_LIFETIME = 0.9;

/** Hit-flash lifetime. */
const FLASH_LIFETIME = 0.45;

/** Flash sprite end scale. */
const FLASH_SCALE_END = 2.6;

/** Number of jitter segments in the trail jagged line. */
const TRAIL_NUM_SEGMENTS = 6;

// ─────────────────────────────────────────────────────────────────────────────
// Internal emitter state
// ─────────────────────────────────────────────────────────────────────────────

interface EmitterState {
  handle: ReturnType<ItemVfxPoolDeps['registry']['create']>;
  finished: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build jagged points between two 3D positions using perpendicular jitter
 * in the XZ plane. Adapted from thunder-fist.ts pattern (written inline
 * per task scope — do NOT import from thunder-fist).
 */
function buildJaggedTrailPoints(
  from: { x: number; y: number; z: number },
  to: { x: number; y: number; z: number },
): THREE.Vector3[] {
  const vFrom = new THREE.Vector3(from.x, from.y, from.z);
  const vTo = new THREE.Vector3(to.x, to.y, to.z);

  const dir = new THREE.Vector3().subVectors(vTo, vFrom);
  const dirXZ = new THREE.Vector3(dir.x, 0, dir.z);
  const up = new THREE.Vector3(0, 1, 0);

  let perp: THREE.Vector3;
  if (dirXZ.lengthSq() < 1e-8) {
    // Effectively vertical segment — any XZ direction works
    perp = new THREE.Vector3(1, 0, 0);
  } else {
    perp = new THREE.Vector3().crossVectors(dirXZ.normalize(), up).normalize();
  }

  const totalPts = TRAIL_NUM_SEGMENTS + 1;
  const points: THREE.Vector3[] = [];
  for (let i = 0; i < totalPts; i++) {
    const t = i / (totalPts - 1);
    const pt = new THREE.Vector3().lerpVectors(vFrom, vTo, t);
    if (i > 0 && i < totalPts - 1) {
      const r = (Math.random() - 0.5) * 0.3;
      pt.x += r * perp.x;
      pt.z += r * perp.z;
    }
    points.push(pt);
  }
  return points;
}

/**
 * Build a LineSegments geometry from a point array (pairs: [p0,p1, p1,p2, …]).
 * Duplicates interior points so LineSegments draws a contiguous polyline.
 */
function buildLineSegmentsFromPoints(
  points: THREE.Vector3[],
  material: THREE.LineBasicMaterial,
): THREE.LineSegments {
  const pairs: THREE.Vector3[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    pairs.push(points[i].clone());
    pairs.push(points[i + 1].clone());
  }
  const geometry = new THREE.BufferGeometry().setFromPoints(pairs);
  return new THREE.LineSegments(geometry, material);
}

function makeElectricSprite(
  position: { x: number; y: number; z: number },
  scale: number,
  opacity = 1,
): { sprite: THREE.Sprite; material: THREE.SpriteMaterial } {
  const material = new THREE.SpriteMaterial({
    color: ELECTRIC_COLOUR,
    transparent: true,
    opacity,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const sprite = new THREE.Sprite(material);
  sprite.position.set(position.x, position.y, position.z);
  sprite.scale.set(scale, scale, 1);
  return { sprite, material };
}

function buildTubeBetween(
  from: { x: number; y: number; z: number },
  to: { x: number; y: number; z: number },
  radius: number,
  material: THREE.Material,
): THREE.Mesh {
  const curve = new THREE.CatmullRomCurve3([
    new THREE.Vector3(from.x, from.y, from.z),
    new THREE.Vector3((from.x + to.x) / 2, Math.max(from.y, to.y) + 0.08, (from.z + to.z) / 2),
    new THREE.Vector3(to.x, to.y, to.z),
  ]);
  return new THREE.Mesh(new THREE.TubeGeometry(curve, 8, radius, 8, false), material);
}

function resolveDashNodePath(dash: ItemDashEvent): string[] {
  const path = Array.isArray(dash.path) ? dash.path.filter((nodeId) => nodeId !== '') : [];
  const resolved: string[] = [];

  if (dash.fromNodeId != null && dash.fromNodeId !== '') {
    resolved.push(dash.fromNodeId);
  }
  for (const nodeId of path) {
    if (resolved.at(-1) !== nodeId) resolved.push(nodeId);
  }
  if (path.length === 0 && dash.toNodeId != null && dash.toNodeId !== '') {
    if (resolved.at(-1) !== dash.toNodeId) resolved.push(dash.toNodeId);
  }

  return resolved;
}

// ─────────────────────────────────────────────────────────────────────────────
// Emitter builders
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Cast aura: electric flash sprite at caster position.
 * Opacity 0 → 1 → 0 over CAST_LIFETIME (200ms).
 */
function buildCastAuraEmitter(
  casterPos: { x: number; y: number; z: number },
  deps: ItemVfxPoolDeps,
  state: EmitterState,
): THREE.Group {
  const { registry } = deps;
  const group = new THREE.Group();

  const auraMaterial = new THREE.SpriteMaterial({
    color: ELECTRIC_COLOUR,
    transparent: true,
    opacity: 0.0,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const auraSprite = new THREE.Sprite(auraMaterial);
  auraSprite.position.set(casterPos.x, casterPos.y, casterPos.z);
  auraSprite.scale.set(0.9, 0.9, 1);
  group.add(auraSprite);

  const coreMaterial = new THREE.SpriteMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const coreSprite = new THREE.Sprite(coreMaterial);
  coreSprite.position.set(casterPos.x, casterPos.y + 0.08, casterPos.z);
  coreSprite.scale.set(0.45, 0.45, 1);
  group.add(coreSprite);

  const handle = registry.create('lightning_dash_cast');
  state.handle = handle;
  const tl = handle.timeline;

  // Flash in then out over CAST_LIFETIME
  const opProxy = { value: 0.0 };
  tl.to(
    opProxy,
    {
      value: 1.0,
      duration: CAST_LIFETIME * 0.4,
      ease: 'power2.out',
      onUpdate: () => {
        auraMaterial.opacity = opProxy.value;
        coreMaterial.opacity = opProxy.value;
      },
    },
    0,
  );
  tl.to(auraSprite.scale, { x: 2.8, y: 2.8, z: 1, duration: CAST_LIFETIME, ease: 'power2.out' }, 0);
  tl.to(coreSprite.scale, { x: 1.4, y: 1.4, z: 1, duration: CAST_LIFETIME, ease: 'power2.out' }, 0);
  tl.to(
    opProxy,
    {
      value: 0.0,
      duration: CAST_LIFETIME * 0.6,
      ease: 'power2.in',
      onUpdate: () => {
        auraMaterial.opacity = opProxy.value;
        coreMaterial.opacity = opProxy.value;
      },
    },
    CAST_LIFETIME * 0.4,
  );

  tl.call(
    () => {
      state.finished = true;
    },
    undefined,
    CAST_LIFETIME,
  );

  return group;
}

/**
 * Trail: jagged line connecting path positions with electric jitter.
 * Opacity 1 → 0 over TRAIL_LIFETIME (600ms). Single-instance per casterId.
 *
 * If path has <2 positions, falls back to a small sprite at the first
 * (or origin) to avoid empty geometry.
 */
function buildTrailEmitter(
  path: ReadonlyArray<{ x: number; y: number; z: number }>,
  deps: ItemVfxPoolDeps,
  state: EmitterState,
): THREE.Group {
  const { registry } = deps;
  const group = new THREE.Group();

  if (path.length >= 2) {
    const outerLineMaterial = new THREE.LineBasicMaterial({
      color: ELECTRIC_COLOUR,
      transparent: true,
      opacity: 1.0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const coreLineMaterial = new THREE.LineBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 1.0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const ribbonMaterial = new THREE.MeshBasicMaterial({
      color: ELECTRIC_COLOUR,
      transparent: true,
      opacity: 0.72,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const coreRibbonMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.52,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    for (let i = 0; i < path.length - 1; i++) {
      const segPoints = buildJaggedTrailPoints(path[i], path[i + 1]);
      group.add(buildLineSegmentsFromPoints(segPoints, outerLineMaterial));
      group.add(
        buildLineSegmentsFromPoints(buildJaggedTrailPoints(path[i], path[i + 1]), coreLineMaterial),
      );
      group.add(buildTubeBetween(path[i], path[i + 1], 0.055, ribbonMaterial));
      group.add(buildTubeBetween(path[i], path[i + 1], 0.022, coreRibbonMaterial));
    }

    const afterimageMaterials: THREE.SpriteMaterial[] = [];
    for (let i = 1; i < path.length; i++) {
      const anchor = path[i];
      const { sprite, material } = makeElectricSprite(
        { x: anchor.x, y: anchor.y + 0.36, z: anchor.z },
        0.95,
        0.52,
      );
      sprite.name = `lightning_dash_afterimage:${i}`;
      group.add(sprite);
      afterimageMaterials.push(material);
    }

    const sparkMaterials: THREE.SpriteMaterial[] = [];
    for (let i = 0; i < Math.min(path.length, 8); i++) {
      const anchor = path[i];
      const { sprite, material } = makeElectricSprite(
        {
          x: anchor.x + (Math.random() - 0.5) * 0.38,
          y: anchor.y + 0.22 + Math.random() * 0.35,
          z: anchor.z + (Math.random() - 0.5) * 0.38,
        },
        0.38,
        0.85,
      );
      sprite.name = `lightning_dash_spark:${i}`;
      group.add(sprite);
      sparkMaterials.push(material);
    }

    const handle = registry.create('lightning_dash_trail');
    state.handle = handle;
    const tl = handle.timeline;

    const opProxy = { value: 1.0 };
    tl.to(
      opProxy,
      {
        value: 0.0,
        duration: TRAIL_LIFETIME,
        ease: 'power1.in',
        onUpdate: () => {
          outerLineMaterial.opacity = opProxy.value;
          coreLineMaterial.opacity = opProxy.value;
          ribbonMaterial.opacity = opProxy.value * 0.72;
          coreRibbonMaterial.opacity = opProxy.value * 0.52;
          for (const material of afterimageMaterials) material.opacity = opProxy.value * 0.52;
          for (const material of sparkMaterials) material.opacity = opProxy.value * 0.85;
        },
      },
      0,
    );
    for (const child of group.children) {
      if (child.name.startsWith('lightning_dash_afterimage')) {
        tl.to(
          child.scale,
          { x: 1.35, y: 1.35, z: 1, duration: TRAIL_LIFETIME, ease: 'power2.out' },
          0,
        );
      }
      if (child.name.startsWith('lightning_dash_spark')) {
        tl.to(child.scale, { x: 0.75, y: 0.75, z: 1, duration: 0.34, ease: 'power2.out' }, 0);
      }
    }

    tl.call(
      () => {
        state.finished = true;
      },
      undefined,
      TRAIL_LIFETIME,
    );
  } else {
    // Degenerate case: single point or empty path → fallback sprite
    const anchor = path[0] ?? { x: 0, y: 0, z: 0 };
    const fallbackMaterial = new THREE.SpriteMaterial({
      color: ELECTRIC_COLOUR,
      transparent: true,
      opacity: 1.0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const sprite = new THREE.Sprite(fallbackMaterial);
    sprite.position.set(anchor.x, anchor.y, anchor.z);
    sprite.scale.set(1.2, 1.2, 1);
    group.add(sprite);

    const handle = registry.create('lightning_dash_trail');
    state.handle = handle;
    const tl = handle.timeline;

    const opProxy = { value: 1.0 };
    tl.to(
      opProxy,
      {
        value: 0.0,
        duration: TRAIL_LIFETIME,
        ease: 'power1.in',
        onUpdate: () => {
          fallbackMaterial.opacity = opProxy.value;
        },
      },
      0,
    );

    tl.call(
      () => {
        state.finished = true;
      },
      undefined,
      TRAIL_LIFETIME,
    );
  }

  return group;
}

/**
 * Hit-flash: sprite at victim position. Scale 0→1.5, opacity 1→0 over 200ms.
 */
function buildHitFlashEmitter(
  targetPos: { x: number; y: number; z: number },
  deps: ItemVfxPoolDeps,
  state: EmitterState,
): THREE.Group {
  const { registry } = deps;
  const group = new THREE.Group();

  const flashMaterial = new THREE.SpriteMaterial({
    color: ELECTRIC_COLOUR,
    transparent: true,
    opacity: 1.0,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const flash = new THREE.Sprite(flashMaterial);
  flash.position.set(targetPos.x, targetPos.y, targetPos.z);
  flash.scale.set(0, 0, 1);
  group.add(flash);

  const coreMaterial = new THREE.SpriteMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 1.0,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const core = new THREE.Sprite(coreMaterial);
  core.position.set(targetPos.x, targetPos.y + 0.05, targetPos.z);
  core.scale.set(0, 0, 1);
  group.add(core);

  const sparkMaterials: THREE.SpriteMaterial[] = [];
  const sparkSprites: THREE.Sprite[] = [];
  for (let i = 0; i < 5; i++) {
    const angle = (i / 5) * Math.PI * 2;
    const { sprite, material } = makeElectricSprite(
      {
        x: targetPos.x + Math.cos(angle) * 0.18,
        y: targetPos.y + 0.18,
        z: targetPos.z + Math.sin(angle) * 0.18,
      },
      0.28,
      0.9,
    );
    group.add(sprite);
    sparkSprites.push(sprite);
    sparkMaterials.push(material);
  }

  const handle = registry.create('lightning_dash_hit_flash');
  state.handle = handle;
  const tl = handle.timeline;

  // Scale 0 → FLASH_SCALE_END
  tl.to(
    flash.scale,
    {
      x: FLASH_SCALE_END,
      y: FLASH_SCALE_END,
      z: 1,
      duration: FLASH_LIFETIME,
      ease: 'power2.out',
    },
    0,
  );
  tl.to(core.scale, { x: 1.5, y: 1.5, z: 1, duration: FLASH_LIFETIME, ease: 'power2.out' }, 0);
  for (const spark of sparkSprites) {
    tl.to(spark.scale, { x: 0.75, y: 0.75, z: 1, duration: FLASH_LIFETIME, ease: 'power2.out' }, 0);
  }

  // Opacity 1 → 0
  const opProxy = { value: 1.0 };
  tl.to(
    opProxy,
    {
      value: 0.0,
      duration: FLASH_LIFETIME,
      ease: 'power2.in',
      onUpdate: () => {
        flashMaterial.opacity = opProxy.value;
        coreMaterial.opacity = opProxy.value;
        for (const material of sparkMaterials) material.opacity = opProxy.value * 0.9;
      },
    },
    0,
  );

  tl.call(
    () => {
      state.finished = true;
    },
    undefined,
    FLASH_LIFETIME,
  );

  return group;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory — createLightningDashPool
// ─────────────────────────────────────────────────────────────────────────────

export function createLightningDashPool(deps: ItemVfxPoolDeps): ItemVfxPool {
  const { scene } = deps;

  /** Cast aura emitters: one-shot per cast. Max-active = 2. */
  const castStates = new Map<THREE.Object3D, EmitterState>();

  /**
   * Trail Map: single-instance per casterId.
   * Key: casterId (string). Value: {emitter, state}.
   * F6 release-first: new acquire for same casterId evicts prior trail.
   */
  const trailMap = new Map<string, { emitter: THREE.Object3D; state: EmitterState }>();

  /** Hit-flash emitters: one-shot per victim. Max-active = 8. */
  const flashStates = new Map<THREE.Object3D, EmitterState>();

  let disposed = false;

  // ── acquire ──────────────────────────────────────────────────────────────

  function acquire(args: ItemVfxSpawnArgs): THREE.Object3D | null {
    if (disposed) return null;

    const { phase } = args;

    if (phase === 'cast') {
      // ── Cast aura (one-shot, max=2) ──────────────────────────────────────
      if (castStates.size >= MAX_CAST_ACTIVE) return null;
      const casterPos = args.casterPosition ?? { x: 0, y: 0, z: 0 };
      const state: EmitterState = { handle: null!, finished: false };
      const group = buildCastAuraEmitter(casterPos, deps, state);
      scene.add(group);
      castStates.set(group, state);
      return group;
    } else if (phase === 'active') {
      // ── Trail (single-instance per casterId, F6 release-first) ───────────
      const trailKey = args.casterId ?? '';

      // F6: release prior trail for this casterId before acquiring new one
      const prior = trailMap.get(trailKey);
      if (prior) {
        prior.state.handle.dispose();
        scene.remove(prior.emitter);
        disposeSkillshotGroup(prior.emitter);
        trailMap.delete(trailKey);
      }

      const path = args.path ?? [];
      const state: EmitterState = { handle: null!, finished: false };
      const group = buildTrailEmitter(path, deps, state);
      scene.add(group);
      trailMap.set(trailKey, { emitter: group, state });
      return group;
    } else if (phase === 'impact') {
      // ── Hit-flash (one-shot per victim, max=8) ──────────────────────────
      if (flashStates.size >= MAX_FLASH_ACTIVE) return null;
      const targetPos = args.targetPosition ?? { x: 0, y: 0, z: 0 };
      const state: EmitterState = { handle: null!, finished: false };
      const group = buildHitFlashEmitter(targetPos, deps, state);
      scene.add(group);
      flashStates.set(group, state);
      return group;
    }

    return null;
  }

  // ── update ───────────────────────────────────────────────────────────────
  // Registry tick contract (HF1): pool.update does NOT advance the registry.

  function update(_deltaSeconds: number): ReadonlySet<THREE.Object3D> {
    const all = new Set<THREE.Object3D>();
    for (const emitter of castStates.keys()) all.add(emitter);
    for (const { emitter } of trailMap.values()) all.add(emitter);
    for (const emitter of flashStates.keys()) all.add(emitter);
    return all;
  }

  // ── isFinished ───────────────────────────────────────────────────────────

  function isFinished(emitter: THREE.Object3D): boolean {
    const castState = castStates.get(emitter);
    if (castState) return castState.finished;

    for (const { emitter: te, state } of trailMap.values()) {
      if (te === emitter) return state.finished;
    }

    const flashState = flashStates.get(emitter);
    if (flashState) return flashState.finished;

    return true; // unknown emitter
  }

  // ── release ──────────────────────────────────────────────────────────────

  function release(emitter: THREE.Object3D): void {
    // Check cast aura
    const castState = castStates.get(emitter);
    if (castState) {
      castState.handle.dispose();
      castStates.delete(emitter);
      scene.remove(emitter);
      disposeSkillshotGroup(emitter);
      return;
    }

    // Check trail map
    for (const [key, { emitter: te, state }] of trailMap.entries()) {
      if (te === emitter) {
        state.handle.dispose();
        scene.remove(emitter);
        disposeSkillshotGroup(emitter);
        trailMap.delete(key);
        return;
      }
    }

    // Check hit-flash
    const flashState = flashStates.get(emitter);
    if (flashState) {
      flashState.handle.dispose();
      flashStates.delete(emitter);
      scene.remove(emitter);
      disposeSkillshotGroup(emitter);
    }
  }

  // ── dispose (F4) ─────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    // Dispose all cast aura emitters
    for (const [emitter, state] of castStates) {
      state.handle.dispose();
      scene.remove(emitter);
      disposeSkillshotGroup(emitter);
    }
    castStates.clear();

    // Dispose all trail emitters
    for (const { emitter, state } of trailMap.values()) {
      state.handle.dispose();
      scene.remove(emitter);
      disposeSkillshotGroup(emitter);
    }
    trailMap.clear();

    // Dispose all hit-flash emitters
    for (const [emitter, state] of flashStates) {
      state.handle.dispose();
      scene.remove(emitter);
      disposeSkillshotGroup(emitter);
    }
    flashStates.clear();
  }

  return { acquire, update, isFinished, release, dispose };
}

// ─────────────────────────────────────────────────────────────────────────────
// Event resolver — resolveLightningDashForEvent
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Resolves Lightning Dash VFX events.
 *
 * Optional position callbacks (Task 6.1 wires real positions):
 *   getPositionForPlayer?(playerId) → resolves victim/caster world position.
 *   getPositionForNode?(nodeId)     → resolves board node world position.
 *
 * Discriminator:
 *   item_cast      → phase='cast' (cast aura)
 *   item_dash      → phase='active' (path trail; path materialized via getPositionForNode)
 *   item_dash_hit  → phase='impact' (per-victim flash; position from getPositionForPlayer)
 */
export function resolveLightningDashForEvent(
  event: GameEvent,
  getPositionForPlayer?: (playerId: string) => { x: number; y: number; z: number } | undefined,
  getPositionForNode?: (nodeId: string) => { x: number; y: number; z: number } | undefined,
): { poolId: 'lightning_dash'; args: ItemVfxSpawnArgs } | null {
  if (event.type === 'item_cast') {
    if (event.itemId !== 'lightning_dash') return null;
    const cast = event as ItemCastEvent;
    const casterPos =
      cast.casterId && getPositionForPlayer ? getPositionForPlayer(cast.casterId) : undefined;
    return {
      poolId: 'lightning_dash',
      args: {
        phase: 'cast',
        casterId: cast.casterId,
        casterPosition: casterPos,
      },
    };
  }

  if (event.type === 'item_dash') {
    const dash = event as ItemDashEvent;
    if (dash.itemId !== 'lightning_dash') return null;

    // Materialize node IDs → world positions (filter out unresolvable nodes)
    let materializedPath: ReadonlyArray<{ x: number; y: number; z: number }> | undefined;
    const nodePath = resolveDashNodePath(dash);
    if (getPositionForNode && nodePath.length > 0) {
      const resolved = nodePath
        .map((nodeId) => getPositionForNode(nodeId))
        .filter((pos): pos is { x: number; y: number; z: number } => pos !== undefined);
      materializedPath = resolved.length > 0 ? resolved : undefined;
    }

    return {
      poolId: 'lightning_dash',
      args: {
        phase: 'active',
        casterId: dash.casterId,
        path: materializedPath,
      },
    };
  }

  if (event.type === 'item_dash_hit') {
    const hit = event as ItemDashHitEvent;
    if (hit.itemId !== 'lightning_dash') return null;
    const victimPos =
      hit.victimId && getPositionForPlayer ? getPositionForPlayer(hit.victimId) : undefined;
    const nodePos =
      victimPos == null && hit.nodeId != null && getPositionForNode
        ? getPositionForNode(hit.nodeId)
        : undefined;
    return {
      poolId: 'lightning_dash',
      args: {
        phase: 'impact',
        casterId: hit.casterId,
        targetPosition: victimPos ?? nodePos,
      },
    };
  }

  return null;
}
